import {
    Compiler,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    SystemJsNgModuleLoader,
    ViewChild,
    ElementRef,
    NgZone
} from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { FormControl } from "@angular/forms";
import {
    ITCIApplication,
    ITCIApplicationDeploymentProvider,
    ITCIApplicationDeployRequest,
    ITCIApplicationDeployResponse,
    ScribeAppStatus,
    TCI_APP_FEATURE_TYPE,
    TCI_APPLICATION_ERR_TYPE,
    TCI_TENANT_SERVICE_PROVIDER_TYPE,
    TCIApplicationDeploymentMode,
    TCIApplicationDeploymentNotification,
    TCIAppStatusCategory,
    TCITenantAppType,
    TCIApplicationDeployConfirmationType
} from "@tci/common";
import { BaseAppDetailEnvService } from "../ng-app-details/ng-app-tabs/ng-apps-env/base-app-detail-env.service";
import { NgToastrService } from "../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { TciTenantService } from "../ng-tenant/tci-tenant.service";
import { TciTenantModelService } from "../ng-tenant/tci-tenant.model";
import { AbstractTciTenantComponentLoader } from "../ng-tenant/tci-tenant-component-loader.component";
import { TCITenantRemoteServices } from "../ng-tenant/tci-tenant-remote-services";
import { TCITenantComponentInput } from "../ng-tenant/tci-tenant-components";
import { TCITenantLoaderComponent } from "../ng-tenant/tci-tenant-component";
import { NgToolsService } from "../ng-shared/ng-util/ng-tools.service";
import { NgAppDeployService } from "./ng-app-deploy.service";
import { NgAppFeatureToggleService } from "../ng-shared/ng-services/ng-app-feature-toggle.service";
import { NgAppChangeNotificationService } from "../ng-shared/ng-services/ng-app-change-notification.service";
import { Observable, Subscription } from "rxjs";
import * as lodash from "lodash";

@TCITenantLoaderComponent({
    isdynamic: true
})
@Component({
    selector: "ng-tenant-dyn-cmp",
    template: "<template #dynamic></template>"
})
export class TCITenantMiniDetailsDynLoaderComponent extends AbstractTciTenantComponentLoader<any, TCITenantComponentInput, any> {
    constructor(
        loader: SystemJsNgModuleLoader,
        compiler: Compiler,
        tenantRemoteServices: TCITenantRemoteServices,
        tenantSvc: TciTenantService,
        tenantModelSvc: TciTenantModelService
    ) {
        super(loader, compiler, tenantRemoteServices, tenantSvc, tenantModelSvc);
    }
}

@Component({
    selector: "ng-app-deploy",
    templateUrl: "./ng-app-deploy.component.html",
    styleUrls: ["./ng-app-deploy.component.css"]
})
export class NgAppDeployComponent implements OnInit, OnDestroy {
    @Input() app: ITCIApplication;
    @Input() mode = TCIApplicationDeploymentMode.MODE_APP_LIST;
    @Output() refreshInstances: EventEmitter<number> = new EventEmitter();
    @Output() scaleUpdating: EventEmitter<boolean> = new EventEmitter();
    @ViewChild("instancesInp") InstancesInput: ElementRef;
    @ViewChild("scaleBtn") ScaleBtn: ElementRef;

    modeIsAppList = true;
    loading = true;  // Determines if the provider is ready
    private _working = false; // Determines if a deploy action is being executed

    get working() {
        return this._working;
    }

    set working(val: boolean) {
        this._working = val;
        val ? this.instancesCtrl.disable() : this.instancesCtrl.enable();
        if (!val) {
            this.instancesCtrl.markAsPristine();
        }
    }

    instances = 0;
    desiredInstances = 1;
    autoScale = false; // TODO @rzavala
    desiredInstancesInput: number;
    instancesCtrl = new FormControl();

    // States
    canStart: boolean;
    canStop: boolean;
    canForceStop: boolean;
    canScale: boolean;
    canShowStartStop: boolean;
    canAutoScale: boolean;
    errorState: boolean;
    stateLabel: string;
    stateColor: string;
    stateIcon: string;
    stateClassName: string;

    tenantServiceNotAvailable = false;
    provider: ITCIApplicationDeploymentProvider;
    subscriptions: Subscription[] = [];
    progressPerc: number;

    showMiniDetails = false; // Toggle modal display
    miniDetailsMsg = "";

    // Details handler
    hasDetails: boolean;

    // Error handler
    hasError: boolean;

    // Publish Status Store
    lastResponse: ITCIApplicationDeployResponse;

    // Initial status
    showInitialStatus = false;
    private LABELS = { stopped: "Stopped", notDeployed: "Not deployed", running: "Running", draft: "Draft" };

    // For UM
    private isScalable = false;

    // Confirmation dialog
    private dialogRequestInput: ITCIApplicationDeployRequest;
    showScaleConfirmDialog = false;
    constructor(
        private ngZone: NgZone,
        private router: Router,
        private route: ActivatedRoute,
        private appDeplSvc: NgAppDeployService,
        private tenantSvc: TciTenantService,
        private tenantModelSvc: TciTenantModelService,
        private toastrSvc: NgToastrService,
        private toolsSvc: NgToolsService,
        private appFeatureToggleService: NgAppFeatureToggleService,
        private envCtrlSvc: BaseAppDetailEnvService,
        private appRefreshNotSvc: NgAppChangeNotificationService
    ) {
        this.subscriptions.push(appDeplSvc.getCloseMiniDetailsSubj()
            .filter(appId => this.app.id !== appId)
            .subscribe(() => this.closeMiniDetails())
        );

        this.subscriptions.push(appDeplSvc.getDialogResponseSubj()
            .filter(res => res.success && this.app.id === res.appId)
            .subscribe(res => {
                switch (res.confirmationType) {
                    case TCIApplicationDeployConfirmationType.CONFIRM_IS_ALIVE:
                        this.working = true;
                        this.hasError = false;
                        this.validInstancesSet();
                        this.subscriptions.push(this.provider.stop(this.dialogRequestInput).subscribe());
                        break;
                }
            })
        );
    }

    ngOnInit() {

        if (this.app.onPremise) {
            if (this.app.remoteManagement) {
                this.isScalable = true;
            } else {
                this.isScalable = false;
            }
        } else {
            this.isScalable = true;
        }

        this.setInitialStatus();

        // Update the component's mode
        this.modeIsAppList = lodash.isEqual(this.mode, TCIApplicationDeploymentMode.MODE_APP_LIST);

        // Request tenant information
        this.tenantSvc.onInit().subscribe(() => {
            const tenant = this.tenantModelSvc.getTenant(this.app.type);
            if (tenant) {
                const provider = tenant.getAppDeploymentProvider();
                if (provider) {
                    this.provider = provider;

                    /**
                     * Switch the status retreive mechanism between cloud and on-premise apps
                     */
                    if (!lodash.get(this.app, "onPremise", false)) {
                        this.subscriptions.push(this.initSubscriptions());
                        this.subscriptions.push(this.initStatus().subscribe(() => this.setInstanceCounters()));
                        this.subscriptions.push(this.provider.startStatusSubscription(this.app).subscribe());
                        this.subscriptions.push(this.provider.getStatusSubject()
                            .filter(instResp => {
                                // For IPAS-16025: filter data, update instance number only when instResp.appId is equal to current app id
                                return instResp.appId === this.app.id &&
                                    (instResp.notificationType === TCIApplicationDeploymentNotification.NOTIFICATION_REFRESH ||
                                        (instResp.desiredInstances > 0 && instResp.instances > 0));
                            })
                            .subscribe(instResp => {
                                // Note: update instances number on anywhere, include app list and app detail
                                this.liveUpdateInstances(instResp);
                            }));
                    } else {
                        this.setOnPremiseStatus();
                    }
                } else {
                    console.warn("No deployer provider for this app type", this.app.type.toString());
                    this.tenantServiceNotAvailable = true;
                }
            } else {
                console.warn("No tenant available for this app type", this.app.type.toString());
                this.tenantServiceNotAvailable = true;
            }
        });
    }

    ngOnDestroy() {
        if (this.provider) {
            this.provider.stopStatusSubscription(this.app).subscribe();
            this.provider.stopOnPremiseStatusSubscription(this.app).subscribe();
        }

        this.subscriptions
            .filter(s => s && !s.closed)
            .forEach(s => s.unsubscribe());
    }

    checkState(isCheckForceStop = false) {
        this.subscriptions.push(
            this.initStatus(isCheckForceStop)
                .take(1)
                .subscribe(res => {
                    this.subscriptions.push(
                        this.setInstanceCounters()
                            .add(() => {
                                this.scaleUpdating.emit(false);
                                delete this.lastResponse; // Reset the response
                            })
                    );
                })
        );
    }

    initSubscriptions() {

        return this.provider.getStatusSubject()
            .filter(res => res.appId === this.app.id)
            .subscribe(res => {
                if (lodash.isEmpty(res.status.getLabel())) { // If no label then use initial state
                    this.setInitialStatus();
                } else {
                    switch (res.notificationType) {
                        // There's something happening (Scaling/Stopping/Pushing---)
                        case TCIApplicationDeploymentNotification.NOTIFICATION_PROGRESS:
                        case TCIApplicationDeploymentNotification.NOTIFICATION_FORCE_STOP:
                            this.working = true;
                            // For IPAS-16025: instances is current running instance, should always use instances number for UI
                            this.emitRefreshInstances(res.instances);
                            this.scaleUpdating.emit(true);
                            this.updateLabelAndStyle(res.status.getLabel(), res.status.getCategory());
                            this.progressPerc = (res.instances + res.failedInstances) / res.desiredInstances;
                            // Note: when app is in progress states, need to check whether app can be forced stopped or not
                            this.checkState(true);
                            break;

                        // "Completed" state, either "Running" or "Stopped" (for TCI)
                        case TCIApplicationDeploymentNotification.NOTIFICATION_REFRESH:
                            this.lastResponse = res;
                            this.updateLabelAndStyle(res.status.getLabel(), res.status.getCategory(), true);
                            this.working = false;
                            this.checkState();
                            break;

                        // This is used for "Failure/Error" states
                        case TCIApplicationDeploymentNotification.NOTIFICATION_STATUS:
                            this.lastResponse = res;
                            this.updateLabelAndStyle(res.status.getLabel(), res.status.getCategory(), true);
                            if (!res.success) {
                                this.checkState();
                                if (!this.modeIsAppList) {
                                    this.working = false;
                                    // this.toastrSvc.error("App scaling failed", res.msg);
                                }
                            } else {
                                if (res.desiredInstances > 0) { // Only for "start"
                                    this.progressPerc = 1;
                                }
                            }
                            break;
                    }
                }
                this.validInstancesSet(); // To enable/disable the button
            });
    }

    initStatus(isCheckForceStop = false) {
        const input = this.getProvisionerRequest();
        const evaluateState = (res: {
            canStart: boolean,
            canStop: boolean,
            canScale: boolean,
            canShowDetails: boolean,
            canShowStartStop: boolean,
            canAutoScale: boolean,
            showStatusError: boolean
        }) => {
            this.ngZone.run(() => {
                this.canStart = res.canStart;
                this.canStop = res.canStop;
                this.canScale = res.canScale;
                this.canShowStartStop = res.canShowStartStop;
                this.canAutoScale = res.canAutoScale;
                this.hasError = res.showStatusError;

                this.loading = false;
                this.canForceStop = false;

                this.working = !this.lastResponse && !res.canStop && !res.canStart && !res.showStatusError;
                // Note: If is checking force stop status and app can be stopped, it means app is in progress but can be force stop
                // will set canForceStop to true, set working = false to make sure stop button is enabled.
                if (isCheckForceStop && res.canStop) {
                    this.canForceStop = true;
                    this.working = false;
                }

                // Display details
                this.hasDetails = !!res.canShowDetails;
            });
            return res;
        };

        return Observable.combineLatest(
            this.provider.canStart(input),
            this.provider.canStop(input),
            this.provider.canScale(input),
            this.provider.canShowDetails(input),
            this.provider.canShowStartStop(input),
            this.appFeatureToggleService.getAppFeatures()
                .map(res => res[TCI_APP_FEATURE_TYPE.APP_AUTO_SCALE].enabled)
                .flatMap(res => res ? this.provider.canAutoScale(input) : Observable.of(false)),
            this.provider.showStatusError(input),
            (canStart, canStop, canScale, canShowDetails, canShowStartStop, canAutoScale, showStatusError) => (
                { canStart, canStop, canScale, canShowDetails, canShowStartStop, canAutoScale, showStatusError }
            ))
            .map(res => evaluateState(res))
            .take(1);
    }

    setInstanceCounters(): Subscription { // Retreive the desired instances from tenant
        return this.provider.getInstances(this.getProvisionerRequest())
            .take(1)
            .subscribe(
                instResp => {
                    if (this.instancesCtrl.pristine) { // If field is touched do not update
                        this.liveUpdateInstances(instResp);
                    }
                    return instResp;
                });
    }

    getProvisionerRequest(): ITCIApplicationDeployRequest {  // Returns an object to be used as provisioner's methods input argument
        return {
            application: this.app,
            mode: this.mode,
            appId: this.app.id,
            tenantType: this.app.type,
            num_instances: this.desiredInstances === 0 ? 1 : this.desiredInstances
        };
    }

    changeState() {
        this.hasDetails = false;
        if (this.working || (this.ScaleBtn && this.ScaleBtn.nativeElement.disabled)) {
            return;
        }

        // GAL do not have a way to stablish the num, so 1 as minimum
        if (isNaN(this.desiredInstancesInput)) {
            this.setInstancesInputValue(0);
        }

        const requestInput = this.getProvisionerRequest();
        requestInput.force_stop = this.canForceStop;
        let subjSubs: Subscription;

        const stopCheck = () => {
            this.provider.canShowConfirmationDialog(requestInput)
                .subscribe(res => {
                    if (res.confirmationType === TCIApplicationDeployConfirmationType.CONFIRM_DEFAULT) {
                        subjSubs = this.provider.stop(requestInput).subscribe();
                    } else {
                        requestInput.confirmationType = res.confirmationType;
                        switch (res.confirmationType) {
                            case TCIApplicationDeployConfirmationType.CONFIRM_IS_ALIVE:
                                this.dialogRequestInput = requestInput;
                                this.appDeplSvc.dialogOpenRequest(requestInput);
                                break;
                            default:
                                console.error("Unsupported response from 'canShowConfirmationDialog'", res);
                        }
                    }
                });
        };

        if (this.modeIsAppList) {
            if (this.canStart) {
                if (this.desiredInstances === 0) { // GAL "start"
                    requestInput.num_instances = 1;
                }

                requestInput.scale_direction = true;
                this.desiredInstances = requestInput.num_instances;
                subjSubs = this.provider.start(requestInput).subscribe();
                this.progressPerc = 0;
            } else if (this.canStop) {
                requestInput.num_instances = 0;
                this.desiredInstances = 0;
                stopCheck();
                return;
            } else {
                console.error("App can't start nor stop");
                return;
            }
        } else {
            if (!this.canScale) { // Scribe case (IPAS-14197)
                if (this.canStart) {
                    this.provider.start(requestInput).subscribe();
                } else if (this.canStop) {
                    requestInput.num_instances = 0;
                    stopCheck();
                    return;
                } else {
                    console.error("App can't scale, start nor stop");
                    return;
                }
            } else if (this.desiredInstancesInput > 0) {
                requestInput.num_instances = this.desiredInstancesInput;
                requestInput.scale_direction = this.desiredInstancesInput > this.desiredInstances;

                this.desiredInstances = requestInput.num_instances;
                subjSubs = this.provider.setInstances(requestInput).subscribe();
            } else {
                requestInput.num_instances = 0;
                stopCheck();
                return;
            }
        }

        this.working = true;
        this.hasError = false;
        this.validInstancesSet();
        this.subscriptions.push(subjSubs);
    }

    updateLabelAndStyle(stateLabel: string, statusCategory: TCIAppStatusCategory, refresh = false) {
        if (refresh && this.stateLabel && this.stateLabel !== stateLabel) { // Request app refresh notification if the state changed
            this.appRefreshNotSvc.requestRefresh(this.app, undefined, TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_DEPLOY);
        }

        this.ngZone.run(() => {
            this.stateLabel = stateLabel;
            this.stateColor = statusCategory.Color;
            this.stateIcon = statusCategory.IconUrl;
            this.stateClassName = statusCategory.ClassName;
            this.appDeplSvc.setLastState(this.app.id, stateLabel, statusCategory);
        });

        /**
          * IPAS-17496, FLOGO-6748
          * Will not show mini details when app scale/deploy is in progress and previous status is a failure.
          * Making hasDetails and hasError flag to false to avoid displaying the mini details (hamburger menu and red dot).
          */
        if (this.working) {
            this.hasDetails = false;
            this.hasError = false;
        }
    }

    scaleUp() {
        this.desiredInstancesInput++;
        this.instancesCtrl.markAsDirty();
        this.focusInputField();
        this.toolsSvc.setTimeout(() => this.validInstancesSet());
    }

    scaleDown() {
        if (this.desiredInstancesInput > 0) {
            this.desiredInstancesInput--;
            this.instancesCtrl.markAsDirty();
            this.focusInputField();
            this.toolsSvc.setTimeout(() => this.validInstancesSet());
        }
    }

    focusInputField() {
        this.InstancesInput.nativeElement.focus();
        return this;
    }

    emitRefreshInstances(instances: number) {
        if (instances >= 0) {
            this.refreshInstances.emit(instances);
        }
    }

    // On box input "ESC" press
    resetInstances() {
        this.instancesCtrl.markAsPristine();
        this.setInstancesInputValue(this.desiredInstances);
    }

    // Enable/Disable "Scale" button on box
    validInstancesSet() {
        if (!this.modeIsAppList) {
            this.setInstancesInputValue(this.desiredInstancesInput);
            if (this.ScaleBtn) {
                const disableBtn =
                    this.desiredInstancesInput < 0 ||
                    this.desiredInstancesInput === this.instances ||
                    this.working;

                this.ScaleBtn.nativeElement.disabled = disableBtn;
            }
        }
    }

    // Panel events
    panelId(): string {
        return "panel_" + this.app.id;
    }

    toggleMiniDetails($event) {
        if (this.hasDetails) {
            this.showMiniDetails = !this.showMiniDetails;
            if (this.showMiniDetails) {
                // Close other mini detail panels
                this.appDeplSvc.closeMiniDetails(this.app.id);

                // Set the data for mini details depending on current context
                const info: ITCIApplicationDeployRequest = {
                    application: this.app,
                    appId: this.app.id,
                    mode: this.mode,
                    tenantType: this.app.type,
                    num_instances: this.desiredInstances
                };

                if (this.hasError) {
                    info.error = {
                        errorType: TCI_APPLICATION_ERR_TYPE.ERR_TYPE_ERROR,
                        msg: null
                    };
                }

                this.provider.getDetailContent(info)
                    .subscribe(res => {
                        if (lodash.isString(res)) {
                            this.miniDetailsMsg = res;
                        } else {
                            this.miniDetailsMsg = res.contentText;
                            setTimeout(() => { // To await for HTML to mount
                                this.ngZone.run(() => {
                                    const interactionElement = document.getElementById(res.clickElementId);
                                    if (interactionElement) {
                                        interactionElement.addEventListener("click", () => {
                                            this.router.navigate(res.clickRouteParams.route, {
                                                queryParams: res.clickRouteParams.queryParams,
                                                relativeTo: this.route
                                            });
                                        });
                                    }
                                });
                            });
                        }
                    });
            }
        } else {
            this.showMiniDetails = false;
        }
    }

    closeMiniDetails($event?, appId?: string) {
        if (!appId || appId === this.app.id) {
            this.showMiniDetails = false;
        }
    }

    private liveUpdateInstances(instResp: ITCIApplicationDeployResponse) {
        // Note: show middle status of instance number when app is scaling.
        if (instResp && instResp.instances) {
            this.ngZone.run(() => {
                this.instances = instResp.instances;
            });
        }
        if (this.working && this.instancesCtrl.dirty) {
            return;
        }

        if (instResp) {
            this.ngZone.run(() => {
                this.instances = instResp.instances || 0;
                this.desiredInstances = instResp.desiredInstances || 0;
                /**
                 * https://jira.tibco.com/browse/IPAS-14753
                 * If hasError and instances = 0, means first time app scale error.
                 *      Assign 0.
                 * If hasError and instances > 0, means running app scale error.
                 *      Assign running instances (i.e disiredinstance - failedInstance).
                 * If no hasError, means app successfully scaled to desired instances.
                 *      Assign desiredInstance.
                 */
                const inputValue = this.hasError ? this.instances > 0 ? this.instances : 0 : this.desiredInstances;
                this.setInstancesInputValue(inputValue);
                // For IPAS-16025: instances is current running instance, should always use instances number for UI
                this.emitRefreshInstances(this.instances);
            });
        }
    }

    private setInstancesInputValue(val: number) {
        const num = lodash.toNumber(val);
        const exec = (inputValue: number) => {
            this.desiredInstancesInput = inputValue;
            this.instancesCtrl.setValue(inputValue);
        };

        if (lodash.isNumber(num) && lodash.isFinite(num)) {
            if (num >= 0) {
                exec(num);
            } else if (lodash.isUndefined(this.desiredInstancesInput)) { // Is coming from GAL after requesting scale change
                exec(0);
            }
        } else { // It came as undefined or different due to error
            exec(0);
        }
    }

    private updateInstancesCount($event: KeyboardEvent) {
        // tslint:disable-next-line: deprecation
        const key = $event.key || $event.keyCode;
        if (key === "Escape" || key === "Esc" || key === 27) {
            return;
        }
        this.instancesCtrl.markAsDirty();
        this.validInstancesSet();
    }

    scaleApp() {
        // IPAS-14136 getChanges method is used to detect the changes in env controls tab and show confirmation model before scaling
        this.envCtrlSvc.getChanges(this.app)
            .subscribe(res => {
                if (Object.values(res).some(val => val === true)) {
                    this.showScaleConfirmDialog = true;
                } else {
                    this.changeState();
                }
            });
    }

    dialogResponse(resp: boolean) {
        this.showScaleConfirmDialog = false;
        if (resp) {
            this.envCtrlSvc.cleanChanges();
            this.changeState();
        }
    }

    private setInitialStatus() {
        const initialState = this.appDeplSvc.getLastState(this.app.id);
        if (!lodash.isUndefined(initialState)) {
            this.showInitialStatus = true;
            if ([this.LABELS.stopped, this.LABELS.notDeployed].includes(initialState.label)) {
                this.canStart = true;
            }
            this.updateLabelAndStyle(initialState.label, initialState.category);
        } else if (!lodash.isEmpty(this.app.appInitialStatus)) {
            let statusCategory: TCIAppStatusCategory;
            if (this.app.type.equals(TCITenantAppType.scribe)) {
                this.showInitialStatus = true;
                const status = ScribeAppStatus.getSolutionStatus({ status: this.app.appInitialStatus });
                statusCategory = ScribeAppStatus.getAppStatusCategory(status);
                this.updateLabelAndStyle(status, statusCategory);
            } else {
                this.showInitialStatus = true;
                switch (this.app.appInitialStatus) {
                    case this.LABELS.stopped:
                        statusCategory = TCIAppStatusCategory.NOT_READY;
                        this.canStart = true;
                        break;
                    case this.LABELS.notDeployed:
                        statusCategory = TCIAppStatusCategory.INCOMPLETE;
                        break;
                    case this.LABELS.running:
                        statusCategory = TCIAppStatusCategory.READY;
                        break;
                    case this.LABELS.draft:
                        statusCategory = TCIAppStatusCategory.INCOMPLETE;
                        break;
                    default:
                        statusCategory = TCIAppStatusCategory.IN_PROGRESS;
                }
                this.updateLabelAndStyle(this.app.appInitialStatus, statusCategory);
            }
        }
    }

    /**
     * Initialize Subscription for On-Premise Apps
     * @returns
     */
    initOnPremSubscriptions() {

        return this.provider.getOnPremiseApplicationStatusSubject()
            .filter(res => res.appId === this.app.id)
            .subscribe(res => {
                if (res.instances > 0 && lodash.isEmpty(res.status.getLabel())) { // If no label then use initial state
                    this.setInitialStatus();
                } else {
                    switch (res.notificationType) {
                        // There's something happening (Scaling/Stopping/Pushing---)
                        case TCIApplicationDeploymentNotification.NOTIFICATION_PROGRESS:
                        case TCIApplicationDeploymentNotification.NOTIFICATION_FORCE_STOP:
                            this.working = true;
                            // For IPAS-16025: instances is current running instance, should always use instances number for UI
                            this.emitRefreshInstances(res.instances);
                            this.scaleUpdating.emit(true);
                            this.updateLabelAndStyle(res.status.getLabel(), res.status.getCategory());
                            this.progressPerc = (res.instances + res.failedInstances) / res.desiredInstances;
                            // Note: when app is in progress states, need to check whether app can be forced stopped or not
                            this.checkState(true);
                            break;

                        // "Completed" state, either "Running" or "Stopped" (for TCI)
                        case TCIApplicationDeploymentNotification.NOTIFICATION_REFRESH:
                            this.lastResponse = res;
                            this.updateLabelAndStyle(res.status.getLabel(), res.status.getCategory(), true);
                            this.working = false;
                            this.checkState();
                            break;

                        // This is used for "Failure/Error" states
                        case TCIApplicationDeploymentNotification.NOTIFICATION_STATUS:
                            this.lastResponse = res;
                            this.updateLabelAndStyle(res.status.getLabel(), res.status.getCategory(), true);
                            if (!res.success) {
                                this.checkState();
                                if (!this.modeIsAppList) {
                                    this.working = false;
                                    // this.toastrSvc.error("App scaling failed", res.msg);
                                }
                            } else {
                                if (res.desiredInstances > 0) { // Only for "start"
                                    this.progressPerc = 1;
                                }
                            }
                            break;
                    }
                }
                this.validInstancesSet(); // To enable/disable the button
            });
    }

    /**
     * Set values at template for on-premise apps
     */
    private setOnPremiseStatus() {
        this.subscriptions.push(this.initOnPremSubscriptions());
        this.subscriptions.push(this.initStatus().subscribe(() => this.setInstanceCounters()));
        this.subscriptions.push(this.provider.startOnPremiseStatusSubscription(this.app).subscribe());
        this.subscriptions.push(
            this.provider.getOnPremiseApplicationStatusSubject()
                .filter(instResp => {
                    // For IPAS-16025: filter data, update instance number only when instResp.appId is equal to current app id
                    return instResp.appId === this.app.id &&
                        (instResp.notificationType === TCIApplicationDeploymentNotification.NOTIFICATION_REFRESH ||
                            (instResp.desiredInstances > 0 && instResp.instances > 0));
                })
                .subscribe(instResp => {
                    // Note: update instances number on anywhere, include app list and app detail
                    this.liveUpdateInstances(instResp);
                }));
    }

    copyToClipboard() {
        this.provider.getDetailTextForClipboard(this.getProvisionerRequest())
            .subscribe(text => {
                const copied = () => this.toastrSvc.info(this.app.name, "Summary copied to clipboard");
                const notCopied = () => this.toastrSvc.error(this.app.name, "Summary was not copied to clipboard");

                const altMethod = () => {
                    if (document.queryCommandSupported && document.queryCommandSupported("copy")) {
                        const textarea = document.createElement("textarea");
                        textarea.textContent = text;
                        textarea.style.position = "fixed";  // Prevent scrolling to bottom of page in Microsoft Edge.
                        textarea.style.display = "none";
                        document.body.appendChild(textarea);
                        textarea.select();
                        try {
                            document.execCommand("copy");  // Security exception may be thrown by some browsers.
                            copied();
                        } catch (ex) {
                            console.warn("Copy to clipboard failed.", ex);
                            notCopied();
                        } finally {
                            document.body.removeChild(textarea);
                        }
                    } else {
                        notCopied();
                    }
                };

                Observable.fromPromise((navigator as any).clipboard.writeText(text))
                    .subscribe(
                        () => copied(),
                        () => altMethod()
                    );
            });
    }
}
